import { SearchAlertOrigin } from '@xing-com/crate-common-graphql-types';
import type {
  CreateSearchAlertMutation,
  DeleteSearchAlertMutation,
} from '@xing-com/crate-jobs-graphql';
import { NwtTrackingProps } from '@xing-com/crate-jobs-tracking';

import { useCreateSearchAlert } from './use-create-search-alert';
import { useCurrentSearchAlertStatus } from './use-current-search-alert-id';
import { useDeleteSearchAlert } from './use-delete-search-alert';
import { useSearchAlerts } from './use-search-alerts';
import { useSearchQueryId } from './use-search-query-id';
import { useShowSearchAlertErrorBanner } from './use-show-search-alert-error-banner';

type OnToggleAlertParams = {
  onCreateCompleted?: (data: CreateSearchAlertMutation) => void;
  onDeleteCompleted?: (data: DeleteSearchAlertMutation) => void;
  nwtTrackingOptions?: Omit<Partial<NwtTrackingProps>, 'eventSchema'>;
};

type UseToggleSearchAlertResult = {
  isAlertActive: boolean;
  isLoading: boolean;
  onToggleAlert: (params?: OnToggleAlertParams) => void;
};

export const useToggleSearchAlert = (): UseToggleSearchAlertResult => {
  const { loading: isLoading } = useSearchAlerts();
  const createSearchAlert = useCreateSearchAlert({
    onError: useShowSearchAlertErrorBanner('CREATE', 'jobs_search/serp'),
  });
  const deleteSearchAlert = useDeleteSearchAlert({
    onError: useShowSearchAlertErrorBanner('DELETE', 'jobs_search/serp'),
  });
  const searchQueryId = useSearchQueryId();
  const { searchAlertId: maybeSearchAlertId } = useCurrentSearchAlertStatus();

  const onToggleAlert: UseToggleSearchAlertResult['onToggleAlert'] = (
    params
  ) => {
    const { onCreateCompleted, onDeleteCompleted, nwtTrackingOptions } =
      params ?? {};

    if (maybeSearchAlertId) {
      deleteSearchAlert({
        alertId: maybeSearchAlertId,
        onCompleted: onDeleteCompleted,
        nwtTrackingOptions,
      });
    } else if (searchQueryId) {
      createSearchAlert({
        queryId: searchQueryId,
        nwtTrackingOptions,
        origin: SearchAlertOrigin.Search,
        onCompleted: onCreateCompleted,
      });
    }
  };

  return {
    isAlertActive: !!maybeSearchAlertId,
    isLoading,
    onToggleAlert,
  };
};
